import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useWizard } from "../WizardContext";
import { blockPreviewComponents } from "../utils/PreviewComponentMapping";
import { debounce } from "lodash";
import {
  resizeImage,
  validateImageFile,
  createProgressTracker,
} from "../../../utils/imageUtils";
import { uploadLogo } from "../../../utils/logoUploadService";

/**
 * Step 2: Style Customization Component
 * Allows users to customize global styles that apply across all selected blocks
 */
const StyleCustomizer = () => {
  const {
    globalStyles,
    updateGlobalStyle,
    selectedBlocks,
    blockConfigs,
    logoUpload,
    updateLogoUpload,
    setLogoUploadStatus,
    setLogoUploadError,
    setLogoUploadSuccess,
    isStepValid,
  } = useWizard();

  const [fileInputKey, setFileInputKey] = useState(Date.now());

  // Create debounced update for color changes
  const debouncedColorUpdate = useCallback(
    (key, value) => {
      const debouncedFn = debounce(() => {
        updateGlobalStyle(key, value);
      }, 200);
      debouncedFn();
    },
    [updateGlobalStyle]
  );

  const handleStyleChange = (key, value) => {
    // Store raw value for contactPageUrl - normalization happens during embed code generation
    // Use debounced update for color changes
    if (key.includes("Color")) {
      debouncedColorUpdate(key, value);
    } else {
      updateGlobalStyle(key, value);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setLogoUploadError(validation.error);
      return;
    }

    try {
      // Set initial upload state
      updateLogoUpload({
        filename: file.name,
        uploadStatus: "uploading",
        uploadProgress: 0,
        error: null,
      });

      // Resize image
      const resized = await resizeImage(file);

      // Set preview immediately
      updateLogoUpload({
        previewUrl: resized.base64,
      });
      // Don't update globalStyles.companyLogo here - let the preview URL handle it

      // Create progress tracker
      const progressTracker = createProgressTracker(
        (progress) => updateLogoUpload({ uploadProgress: progress }),
        file.size
      );
      progressTracker.start();

      // Upload using logo upload service
      const uploadResult = await uploadLogo({
        base64: resized.base64,
        filename: file.name,
        contentType: file.type,
      });

      progressTracker.complete();

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || "Upload failed");
      }

      // Update state with success
      setLogoUploadSuccess(uploadResult.url);
    } catch (error) {
      console.error("Logo upload error:", error);
      setLogoUploadError(error.message || "Failed to upload logo");
    }
  };

  const handleRemoveLogo = () => {
    // Reset to default logo
    updateGlobalStyle("companyLogo", "https://tpci.vercel.app/phcg_logo.png");
    updateLogoUpload({
      previewUrl: null,
      blobUrl: null,
      uploadStatus: "idle",
      uploadProgress: 0,
      error: null,
      filename: null,
    });
    // Reset file input
    setFileInputKey(Date.now());
  };

  const handleRetryUpload = () => {
    // Reset error state
    updateLogoUpload({
      uploadStatus: "idle",
      error: null,
      uploadProgress: 0,
    });
  };

  const renderLogoUploadSection = () => {
    const {
      uploadStatus,
      uploadProgress,
      error,
      filename,
      blobUrl,
      previewUrl,
    } = logoUpload;

    if (uploadStatus === "uploading") {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <CircularProgress size={24} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Uploading {filename}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{ mt: 2, mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {uploadProgress}%
          </Typography>
        </Box>
      );
    }

    if (uploadStatus === "success" && (blobUrl || previewUrl)) {
      return (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              bgcolor: "#f5f5f5",
            }}
          >
            <img
              src={blobUrl || previewUrl || globalStyles.companyLogo}
              alt="Company logo"
              style={{
                maxHeight: 60,
                maxWidth: 150,
                marginRight: 16,
                objectFit: "contain",
              }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <CheckCircleIcon
                  sx={{ color: "success.main", fontSize: 20, mr: 1 }}
                />
                <Typography variant="body2" color="success.main">
                  Logo uploaded successfully
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filename}
              </Typography>
            </Box>
            <Tooltip title="Remove logo">
              <IconButton onClick={handleRemoveLogo} size="small">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mt: 2 }}
            startIcon={<RefreshIcon />}
          >
            Change Logo
            <input
              key={fileInputKey}
              type="file"
              hidden
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleLogoUpload}
            />
          </Button>
        </Box>
      );
    }

    if (uploadStatus === "error") {
      return (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ py: 1.5 }}
              startIcon={<CloudUploadIcon />}
            >
              Try Again
              <input
                key={fileInputKey}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLogoUpload}
              />
            </Button>
            <Button
              variant="text"
              fullWidth
              sx={{ py: 1, color: "text.secondary" }}
              onClick={() => {
                updateLogoUpload({
                  uploadStatus: "idle",
                  error: null,
                  uploadProgress: 0,
                });
              }}
            >
              Continue without logo
            </Button>
          </Box>
        </Box>
      );
    }

    // Default idle state
    return (
      <>
        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ py: 1.5 }}
          startIcon={<CloudUploadIcon />}
        >
         
          <input
            key={fileInputKey}
            type="file"
            hidden
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleLogoUpload}
          />
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Upload your logo (up to 1MB). It can be any one of these: PNG, JPG, or
          JPEG.
        </Typography>
      </>
    );
  };

  return (
    <Box sx={{ minHeight: "60vh" }}>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
      >
        Make It Match Your Brand
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: { xs: 3, md: 4 },
          color: "text.secondary",
          fontSize: { xs: "0.875rem", sm: "1rem" },
        }}
      >
        Customize colors, fonts, and your Firm's information to match your
        website.
        {/* Hide on mobile */}
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          {" "}
          You'll see changes instantly in the preview.
        </Box>
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
        {/* Left Panel: Live Preview - wider width like Step 1 */}
        <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
          {selectedBlocks.length > 0 ? (
            <Box
              sx={{
                width: "100%",
                "& > *": {
                  margin: 0,
                  padding: 0,
                  width: "100% !important",
                  maxWidth: "100% !important",
                  boxSizing: "border-box !important",
                },
              }}
            >
              {selectedBlocks.map((block) => {
                const PreviewComponent = blockPreviewComponents[block.id];
                if (!PreviewComponent) return null;

                // Start with block's original config
                const blockConfig = blockConfigs[block.id] || {};

                // Calculate the logo URL to use
                const logoToUse =
                  (logoUpload &&
                    (logoUpload.blobUrl || logoUpload.previewUrl)) ||
                  globalStyles.companyLogo;

                // Logo URL calculation complete

                // Only override specific properties with global styles
                const mergedConfig = {
                  ...blockConfig,
                  // Always apply company name, abbreviation, and logo
                  companyName: globalStyles.companyName,
                  companyAcronym: globalStyles.companyAcronym,
                  // IMPORTANT: Pass contact URL to all blocks - must come after spread to override any undefined
                  contactPageUrl: globalStyles.contactPageUrl || '',
                  buttonUrl: globalStyles.contactPageUrl || '', // Also set buttonUrl for compatibility
                  // Apply logo to all possible logo properties used by different components
                  // BUT exclude charter block's logoSrc to preserve TPCI logo
                  logoUrl: logoToUse,
                  ...(block.id !== "charter" && { logoSrc: logoToUse }),
                  companyLogo: logoToUse,
                  // Override button text for blocks that have hardcoded company references
                  ...(block.id === "PHCG" && { buttonText: null }), // Force dynamic button text
                  ...(block.id === "dypLicensedUser" && {
                    buttonText: null,
                    organizationName: null,
                  }), // Force dynamic text
                  ...(block.id === "pcProcess" && { buttonText: null }), // Force dynamic button text
                  // Apply fonts
                  headingFont: globalStyles.headingFont,
                  bodyFont: globalStyles.bodyFont,
                  // Override colors based on original values - always apply these
                  primaryColor: globalStyles.primaryColor,
                  accentColor: globalStyles.accentColor,
                  ...(blockConfig.buttonColor === "#CE950F" && {
                    buttonColor: globalStyles.accentColor,
                  }),
                  ...(blockConfig.headingColor === "#1C436A" && {
                    headingColor: globalStyles.primaryColor,
                  }),
                  // Override backgroundColor if it matches primary color (like PHCG block)
                  ...(blockConfig.backgroundColor === "#1C436A" && {
                    backgroundColor: globalStyles.primaryColor,
                  }),
                  // Override backgroundColor if it matches accent color (like BookPromo block)
                  ...(blockConfig.backgroundColor === "#CE950F" && {
                    backgroundColor: globalStyles.accentColor,
                  }),
                  // Override overlayColor if it contains the default primary color
                  ...(blockConfig.overlayColor &&
                    blockConfig.overlayColor.includes("28, 67, 106") && {
                      overlayColor: blockConfig.overlayColor.replace(
                        "28, 67, 106",
                        globalStyles.primaryColor
                          .replace("#", "")
                          .match(/.{2}/g)
                          .map((hex) => parseInt(hex, 16))
                          .join(", ")
                      ),
                    }),
                  // Only override text color if it was the default dark text
                  ...(blockConfig.textColor === "#333333" && {
                    textColor: globalStyles.textColor,
                  }),
                  // Apply background color for white backgrounds only
                  ...(blockConfig.backgroundColor === "#ffffff" &&
                    globalStyles.backgroundColor !== "#ffffff" && {
                      backgroundColor: globalStyles.backgroundColor,
                    }),
                };

                return (
                  <PreviewComponent key={block.id} config={mergedConfig} />
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <Typography variant="h6" gutterBottom>
                No blocks selected
              </Typography>
              <Typography variant="body2">
                Go back to Step 1 to select content to preview
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Right Panel: Style Controls - narrower width like Step 1 */}
        <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                textAlign: "center",
              }}
            >
              Brand Customization
            </Typography>
            <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

            {/* Important Customization Note */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: "rgba(232, 244, 253, 0.9)",
                border: "1px solid rgba(66, 165, 245, 0.3)",
                borderRadius: 2,
              }}
            >
              <AlertTitle sx={{ fontWeight: "bold" }}>
                Important Customization Note
              </AlertTitle>
              <Typography variant="body2">
                {globalStyles.companyName
                  ? `You can replace "${globalStyles.companyName}" with your firm's name and logo.`
                  : "You can customize all branding elements with your firm's name and logo."
                } Licensed content sections (marked as TPCI materials) cannot be modified per the license.
              </Typography>
            </Alert>

            {/* Created blocks summary */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Chip
                label={`You created 1 PC webpage with ${selectedBlocks.length} content block${selectedBlocks.length !== 1 ? 's' : ''}.`}
                sx={{
                  bgcolor: '#E3F2FD',
                  color: '#1976D2',
                  fontWeight: 'medium',
                  fontSize: '0.875rem'
                }}
              />
            </Box>

            {/* Company Information */}
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: 3 }}>
                <Box sx={{
                  minWidth: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: '#1C436A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  flexShrink: 0
                }}>
                  1
                </Box>
                <Box sx={{ flex: 1, pt: 0.125 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.0625rem" },
                      color: '#1C436A',
                      mb: 0.5
                    }}
                  >
                    Your Firm's Information
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    Enter your company information to customize all content blocks
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={globalStyles.companyName}
                    onChange={(e) =>
                      handleStyleChange("companyName", e.target.value)
                    }
                    placeholder="Enter Your Firm's Full Name"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#666666',
                        padding: '16px 14px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#999999',
                        opacity: 1,
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={globalStyles.companyAcronym}
                    onChange={(e) =>
                      handleStyleChange("companyAcronym", e.target.value)
                    }
                    placeholder="Enter Your Firm's Abbreviated Name"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#666666',
                        padding: '16px 14px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#999999',
                        opacity: 1,
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
                {/* Hide logo upload if Hero block is selected */}
                {!selectedBlocks.some(block => block.id === 'hero') && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          mb: 0.5,
                          ml: 1.75,
                        }}
                      >
                        Upload Your Firm's Logo
                      </Typography>
                      {renderLogoUploadSection()}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={globalStyles.contactPageUrl}
                    onChange={(e) =>
                      handleStyleChange("contactPageUrl", e.target.value)
                    }
                    placeholder="Contact URL or Email (e.g., https://yoursite.com/contact or email@yourfirm.com)"
                    type="text"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: '#666666',
                        fontFamily: 'monospace',
                        padding: '16px 14px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#999999',
                        opacity: 1,
                        fontFamily: 'monospace',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bbb',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Color Scheme */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: { xs: 3, md: 4 } }}>
              <Box sx={{
                minWidth: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#1C436A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
              }}>
                2
              </Box>
              <Box sx={{ flex: 1, pt: 0.125 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    color: '#1C436A',
                    mb: 0.5
                  }}
                >
                  Your Website's Color Scheme
                </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Primary Color
                    </Typography>
                    <TextField
                      fullWidth
                      type="color"
                      variant="outlined"
                      value={globalStyles.primaryColor}
                      onChange={(e) =>
                        handleStyleChange("primaryColor", e.target.value)
                      }
                      sx={{
                        '& .MuiInputBase-input': {
                          height: '40px',
                          padding: '8px',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Accent Color
                    </Typography>
                    <TextField
                      fullWidth
                      type="color"
                      variant="outlined"
                      value={globalStyles.accentColor}
                      onChange={(e) =>
                        handleStyleChange("accentColor", e.target.value)
                      }
                      sx={{
                        '& .MuiInputBase-input': {
                          height: '40px',
                          padding: '8px',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
                {/* Text Color and Background Color hidden to reduce confusion
                    IMPORTANT: These colors were causing conflicts with primary/accent colors */}
                {/* <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Text Color
                    </Typography>
                    <TextField
                      fullWidth
                      type="color"
                      variant="outlined"
                      value={globalStyles.textColor}
                      onChange={(e) =>
                        handleStyleChange("textColor", e.target.value)
                      }
                      sx={{
                        '& .MuiInputBase-input': {
                          height: '40px',
                          padding: '8px',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Background Color
                    </Typography>
                    <TextField
                      fullWidth
                      type="color"
                      variant="outlined"
                      value={globalStyles.backgroundColor}
                      onChange={(e) =>
                        handleStyleChange("backgroundColor", e.target.value)
                      }
                      sx={{
                        '& .MuiInputBase-input': {
                          height: '40px',
                          padding: '8px',
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid> */}
                </Grid>
              </Box>
            </Box>

            {/* Typography */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mb: { xs: 3, md: 4 } }}>
              <Box sx={{
                minWidth: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#1C436A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
              }}>
                3
              </Box>
              <Box sx={{ flex: 1, pt: 0.125 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    color: '#1C436A',
                    mb: 0.5
                  }}
                >
                  Your Website's Typography
                </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Heading Font
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={globalStyles.headingFont}
                        displayEmpty
                        variant="outlined"
                        onChange={(e) =>
                          handleStyleChange("headingFont", e.target.value)
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            color: '#666666',
                            padding: '16px 14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ddd',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2',
                          },
                        }}
                      >
                      <MenuItem value="serif">Serif</MenuItem>
                      <MenuItem value="sans-serif">Sans-serif</MenuItem>
                      <MenuItem value="monospace">Monospace</MenuItem>
                      <MenuItem value="'Times New Roman', serif">
                        Times New Roman
                      </MenuItem>
                        <MenuItem value="'Arial', sans-serif">Arial</MenuItem>
                        <MenuItem value="'Georgia', serif">Georgia</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#666666',
                        fontSize: '0.75rem',
                        mb: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Body Font
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={globalStyles.bodyFont}
                        displayEmpty
                        variant="outlined"
                        onChange={(e) =>
                          handleStyleChange("bodyFont", e.target.value)
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            color: '#666666',
                            padding: '16px 14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#ddd',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#bbb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2',
                          },
                        }}
                      >
                      <MenuItem value="sans-serif">Sans-serif</MenuItem>
                      <MenuItem value="serif">Serif</MenuItem>
                      <MenuItem value="monospace">Monospace</MenuItem>
                      <MenuItem value="'Arial', sans-serif">Arial</MenuItem>
                      <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
                        <MenuItem value="'Helvetica', sans-serif">
                          Helvetica
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Reset Button */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, mt: 4, pt: 3, borderTop: "1px solid #e0e0e0" }}>
              <Box sx={{
                minWidth: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: '#1C436A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                flexShrink: 0
              }}>
                4
              </Box>
              <Box sx={{ flex: 1, pt: 0.125 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.0625rem" },
                    color: '#1C436A',
                    mb: 2
                  }}
                >
                  Reset Colors & Typography To Start Over
                </Typography>
                <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => {
                  // Reset to default styles
                  updateGlobalStyle("companyName", "");
                  updateGlobalStyle("companyAcronym", "");
                  updateGlobalStyle(
                    "companyLogo",
                    "https://tpci.vercel.app/phcg_logo.png"
                  );
                  updateGlobalStyle("contactPageUrl", "");
                  updateGlobalStyle("primaryColor", "#1C436A");
                  updateGlobalStyle("accentColor", "#CE950F");
                  updateGlobalStyle("textColor", "#333333");
                  updateGlobalStyle("backgroundColor", "#ffffff");
                  updateGlobalStyle("headingFont", "serif");
                  updateGlobalStyle("bodyFont", "sans-serif");
                  // Reset logo upload state
                  handleRemoveLogo();
                }}
                sx={{
                  borderColor: "#999",
                  color: "#666",
                  "&:hover": {
                    borderColor: "#666",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                Reset to Default Colors & Typography
              </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Upload status indicator for Next button */}
      {logoUpload.uploadStatus === "uploading" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please wait for the logo upload to complete before proceeding to the
          next step.
        </Alert>
      )}
    </Box>
  );
};

export default StyleCustomizer;
